import { apiRequest, buildApiUrl, API_CONFIG } from '../utils/api';

// Servicio para manejar proyectos
export class ProjectService {
  // Obtener todos los proyectos
  static async getProjects() {
    try {
      const response = await apiRequest(buildApiUrl(API_CONFIG.ENDPOINTS.PROJECTS), {
        method: 'GET'
      });
      
      if (response.response && response.response.content && response.response.content.projects) {
        return {
          success: true,
          projects: response.response.content.projects,
          operationId: response.operationId
        };
      } else {
        throw new Error('No projects found in response');
      }
    } catch (error) {
      console.error('Error fetching projects:', error);
      return {
        success: false,
        error: error.message,
        projects: []
      };
    }
  }

  // Crear un nuevo proyecto
  static async createProject(projectData) {
    try {
      console.log('🔍 ProjectService - Datos recibidos:', projectData);
      console.log('🔍 Tipo de initialDate:', typeof projectData.initialDate, projectData.initialDate);
      console.log('🔍 Tipo de finalDate:', typeof projectData.finalDate, projectData.finalDate);
      
      const response = await apiRequest(buildApiUrl(API_CONFIG.ENDPOINTS.CREATE_PROJECT), {
        method: 'POST',
        body: JSON.stringify(projectData)
      });
      
      console.log('✅ Respuesta del backend al crear proyecto:', response);
      
      // Intentar extraer el proyecto de diferentes estructuras posibles
      let project = null;
      
      // Estructura 1: response.response.content.project
      if (response.response?.content?.project) {
        project = response.response.content.project;
      }
      // Estructura 2: response.content.project
      else if (response.content?.project) {
        project = response.content.project;
      }
      // Estructura 3: response.project
      else if (response.project) {
        project = response.project;
      }
      // Estructura 4: el response mismo es el proyecto
      else if (response.id && response.name) {
        project = response;
      }
      
      if (project) {
        console.log('✅ Proyecto extraído exitosamente:', project);
        return {
          success: true,
          project: project,
          operationId: response.operationId
        };
      } else {
        console.error('⚠️ No se pudo extraer el proyecto de la respuesta:', response);
        // Aún así considerarlo exitoso si llegó aquí sin error
        return {
          success: true,
          project: {
            id: Date.now(),
            name: projectData.name,
            description: projectData.description,
            status: 'pending',
            initial_date: projectData.initialDate,
            final_date: projectData.finalDate,
            progress: 0,
            tasks: 0
          },
          operationId: response.operationId
        };
      }
    } catch (error) {
      console.error('❌ Error creating project:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  // Actualizar un proyecto
  static async updateProject(projectId, projectData) {
    try {
      const response = await apiRequest(buildApiUrl(`${API_CONFIG.ENDPOINTS.PROJECT}/${projectId}`), {
        method: 'PUT',
        body: JSON.stringify(projectData)
      });
      
      return {
        success: true,
        project: response.response?.content?.project,
        operationId: response.operationId
      };
    } catch (error) {
      console.error('Error updating project:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  // Eliminar un proyecto
  static async deleteProject(projectId) {
    try {
      const response = await apiRequest(buildApiUrl(`${API_CONFIG.ENDPOINTS.PROJECT}/${projectId}`), {
        method: 'DELETE'
      });
      
      return {
        success: true,
        operationId: response.operationId
      };
    } catch (error) {
      console.error('Error deleting project:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  // Obtener un proyecto específico
  static async getProject(projectId) {
    try {
      const response = await apiRequest(buildApiUrl(`${API_CONFIG.ENDPOINTS.PROJECT}/${projectId}`), {
        method: 'GET'
      });
      
      return {
        success: true,
        project: response.response?.content?.project,
        operationId: response.operationId
      };
    } catch (error) {
      console.error('Error fetching project:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }
}

// Función helper para formatear proyectos de la API
export const formatProjectFromApi = (apiProject) => {
  if (!apiProject) {
    console.error('❌ formatProjectFromApi recibió un proyecto nulo o indefinido');
    return null;
  }
  
  // Manejar diferentes formatos de nombres de campos (snake_case y camelCase)
  const initialDate = apiProject.initial_date || apiProject.initialDate;
  const finalDate = apiProject.final_date || apiProject.finalDate;
  const corporationId = apiProject.corporation_id || apiProject.corporationId;
  
  const participants = Array.isArray(apiProject.participants)
    ? apiProject.participants.map((participant) => ({
        id: participant.id?.toString() || '',
        name: participant.name || '',
        last_name: participant.last_name || '',
        email: participant.email || '',
        status: participant.status || 'active'
      }))
    : [];

  const tags = Array.isArray(apiProject.tags)
    ? apiProject.tags.map((tag) => ({
        id: tag.id?.toString() || '',
        name: tag.name || 'Etiqueta',
        category_id: tag.category_id || tag.categoryId || null,
        color: tag.color || '4B5563'
      }))
    : [];

  return {
    id: apiProject.id?.toString() || String(Date.now()),
    name: apiProject.name || 'Sin nombre',
    description: apiProject.description || 'Sin descripción',
    status: apiProject.status || 'pending',
    initialDate: initialDate,
    finalDate: finalDate,
    corporationId: corporationId,
    progress: apiProject.progress || 0,
    tasksCount: apiProject.tasks || apiProject.tasksCount || 0, // Número de tareas del proyecto
    participants,
    tags,
    // Campos adicionales para compatibilidad con el frontend
    color: getProjectColor(apiProject.status || 'pending'),
    icon: getProjectIcon(apiProject.name || ''),
    methodology: 'scrum', // Por defecto, se puede ajustar según la API
    team: [], // Se puede poblar desde otra API
    tasks: [] // Array de tareas detalladas (se puede poblar desde otra API)
  };
};

// Función helper para obtener color según el estado
const getProjectColor = (status) => {
  const colorMap = {
    'pending': 'blue',
    'in-progress': 'green',
    'completed': 'success',
    'backlog': 'gray',
    'cancelled': 'red'
  };
  return colorMap[status] || 'blue';
};

// Función helper para obtener icono según el nombre
const getProjectIcon = (name) => {
  const iconMap = {
    'api': '🔌',
    'login': '🔐',
    'onboarding': '🚀',
    'testing': '🧪',
    'mobile': '📱',
    'web': '🌐',
    'backend': '⚙️',
    'frontend': '🎨'
  };
  
  const lowerName = name.toLowerCase();
  for (const [key, icon] of Object.entries(iconMap)) {
    if (lowerName.includes(key)) {
      return icon;
    }
  }
  
  return '📋'; // Icono por defecto
};
